import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  IconButton,
  Collapse,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Download as DownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Timer as HoursIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  getMonthlySummary,
  getSchoolReport,
  TeacherAttendanceSummaryDto,
  TeacherAttendanceResponse,
} from "../../services/teacherAttendanceService";
import { getAllTeacher, Teacher } from "../../services/teacherService";
import { exportTeacherAttendanceToExcel } from "../../utils/attendanceExport";
import { TeacherAttendanceCalendar } from "./TeacherAttendanceCalendar";

const formatWorkingHours = (totalHours: number | null): string => {
  if (totalHours === null || totalHours === undefined || isNaN(totalHours)) return "0h 0m";
  const totalMinutes = Math.round(totalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

export const AdminMonthlyReport: React.FC = () => {
  const { t } = useTranslation();
  const currentDate = new Date();
  
  const [month, setMonth] = useState<number>(currentDate.getMonth() + 1); // 1-12
  const [year, setYear] = useState<number>(currentDate.getFullYear());
  
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [summaries, setSummaries] = useState<TeacherAttendanceSummaryDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Row expansion state (map of staffId -> boolean)
  const [expandedTeacherId, setExpandedTeacherId] = useState<number | null>(null);

  const months = [
    { value: 1, label: t("January", "January") },
    { value: 2, label: t("February", "February") },
    { value: 3, label: t("March", "March") },
    { value: 4, label: t("April", "April") },
    { value: 5, label: t("May", "May") },
    { value: 6, label: t("June", "June") },
    { value: 7, label: t("July", "July") },
    { value: 8, label: t("August", "August") },
    { value: 9, label: t("September", "September") },
    { value: 10, label: t("October", "October") },
    { value: 11, label: t("November", "November") },
    { value: 12, label: t("December", "December") },
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 2 + i);

  // Fetch list of teachers and their summaries
  const fetchMonthlyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all teachers in the school
      const teacherList = await getAllTeacher();
      setTeachers(teacherList);

      // Fetch summaries in parallel
      const summaryList = await Promise.all(
        teacherList.map(async (teacher) => {
          try {
            return await getMonthlySummary(teacher.id, month, year);
          } catch (err) {
            // Fallback for teachers with no logs in the database
            return {
              staffId: teacher.id,
              staffName: `${teacher.firstName} ${teacher.lastName}`,
              month,
              year,
              totalDaysPresent: 0,
              totalDaysLate: 0,
              totalDaysHalfDay: 0,
              totalDaysIncomplete: 0,
              totalDaysAbsent: 0,
              totalDaysLeave: 0,
              totalHoursWorked: 0,
            };
          }
        })
      );

      setSummaries(summaryList);
    } catch (err: any) {
      setError(err?.response?.data?.message || t("failed_to_load_report_data") || "Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyData();
    setExpandedTeacherId(null);
  }, [month, year]);

  // Handle excel export
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      setError(null);

      // Calculate days in the selected month
      const daysInMonth = new Date(year, month, 0).getDate();
      const fromDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const toDate = `${year}-${String(month).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;

      // Fetch all detailed logs for that month (up to 1000 rows)
      const detailReport = await getSchoolReport(fromDate, toDate, 0, 1000);
      
      // Export to Excel
      exportTeacherAttendanceToExcel(
        summaries,
        detailReport.content,
        month,
        year,
        "Teacher_Attendance_Report"
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || t("failed_to_export_excel") || "Failed to export Excel report");
    } finally {
      setExporting(false);
    }
  };

  const toggleRow = (staffId: number) => {
    setExpandedTeacherId(expandedTeacherId === staffId ? null : staffId);
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Filters & Export (styled like Application Form management) */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Left: Selectors */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t("month", "Month")}</InputLabel>
            <Select
              value={month}
              label={t("month", "Month")}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>{t("year", "Year")}</InputLabel>
            <Select
              value={year}
              label={t("year", "Year")}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Right: Export Button */}
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleExportExcel}
          disabled={loading || exporting || summaries.length === 0}
          sx={{
            backgroundColor: "#3092e3",
            "&:hover": { backgroundColor: "primary.main" },
            textTransform: "none",
            fontWeight: "medium",
          }}
        >
          {exporting ? t("exporting", "Exporting...") : t("export_to_excel", "Export Report")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="60px" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} />
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>{t("teacher_name", "Teacher Name")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("present", "Present")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("late", "Late")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("half_day", "Half Day")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("incomplete", "Incomplete")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("absent", "Absent")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("leave", "Leave")}</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }} align="center">{t("hours_worked", "Hours Worked")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {t("no_teachers_found", "No teachers registered in the system.")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                summaries.map((row) => (
                  <React.Fragment key={row.staffId}>
                    <TableRow hover selected={expandedTeacherId === row.staffId}>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(row.staffId)}
                        >
                          {expandedTeacherId === row.staffId ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {row.staffName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{row.totalDaysPresent}</TableCell>
                      <TableCell align="center" sx={{ color: row.totalDaysLate > 0 ? "warning.main" : "inherit" }}>
                        {row.totalDaysLate}
                      </TableCell>
                      <TableCell align="center" sx={{ color: row.totalDaysHalfDay > 0 ? "orange" : "inherit" }}>
                        {row.totalDaysHalfDay}
                      </TableCell>
                      <TableCell align="center" sx={{ color: row.totalDaysIncomplete > 0 ? "text.secondary" : "inherit" }}>
                        {row.totalDaysIncomplete}
                      </TableCell>
                      <TableCell align="center" sx={{ color: row.totalDaysAbsent > 0 ? "error.main" : "inherit" }}>
                        {row.totalDaysAbsent}
                      </TableCell>
                      <TableCell align="center">{row.totalDaysLeave}</TableCell>
                      <TableCell align="center">
                        <Box display="inline-flex" alignItems="center">
                          <HoursIcon fontSize="inherit" sx={{ mr: 0.5, color: "text.secondary" }} />
                          <Typography variant="body2" fontWeight="medium">
                            {formatWorkingHours(row.totalHoursWorked)}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expandable Details Row */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse
                          in={expandedTeacherId === row.staffId}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 3.5, px: 2, bgcolor: "rgba(0,0,0,0.01)" }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t("monthly_calendar_view", "Attendance Calendar for")} {row.staffName}
                            </Typography>
                            <Box sx={{ maxWidth: "800px", margin: "0 auto", bgcolor: "#fff", p: 2, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                              <TeacherAttendanceCalendar
                                staffId={row.staffId}
                                month={month}
                                year={year}
                              />
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
